import { CircleMarker, Popup, Tooltip, useMap } from "react-leaflet";
import { useState, useEffect, useMemo, type CSSProperties } from "react";
import type { Venue } from "@shared/schema";

// ─── Solid-fill venue type SVG icons ────────────────────────────────────────

function MartiniGlassIcon({ fill, size = 24 }: { fill: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" display="block">
      {/* Single path: bowl → left stem side → base → right stem side → close */}
      <path
        d="M 2 3 L 11 15 L 11 20 L 7 20 L 7 22 L 17 22 L 17 20 L 13 20 L 13 15 L 22 3 Z"
        fill={fill}
      />
    </svg>
  );
}

function PintGlassIcon({ fill, size = 24 }: { fill: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" display="block">
      {/* Trapezoid: wider at top (rim), narrower at base */}
      <path d="M 5 3 L 19 3 L 16 21 L 8 21 Z" fill={fill} />
    </svg>
  );
}

function PartyPopperIcon({ fill, size = 24 }: { fill: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" display="block">
      {/* Horn/cone body — diagonal from lower-left (handle) to upper-right (opening) */}
      <path d="M 3 22 L 6 19 L 20 7 L 16 3 Z" fill={fill} />
      {/* Knot at the narrow end */}
      <circle cx="2.5" cy="21.5" r="2" fill={fill} />
      {/* Burst circle at the opening end */}
      <circle cx="21.5" cy="3" r="2" fill={fill} />
    </svg>
  );
}

// ─── Colour helpers ──────────────────────────────────────────────────────────

function getIconFill(venueClosed: boolean, noData: boolean, capacityLevel: number): string {
  if (venueClosed || noData) return '#9ca3af'; // gray-400 — muted
  if (capacityLevel >= 70) return '#ef4444';   // red-500 — busy
  if (capacityLevel >= 30) return '#f97316';   // orange-500 — moderate
  return '#3b82f6';                             // blue-500 — quiet
}

function getIconFilter(venueClosed: boolean, noData: boolean, capacityLevel: number): string {
  if (venueClosed || noData) {
    // White halo only — no capacity glow for muted states
    return 'drop-shadow(0 0 2px rgba(255,255,255,0.7))';
  }
  if (capacityLevel >= 70) {
    return 'drop-shadow(0 0 2px #fff) drop-shadow(0 0 5px rgba(239,68,68,0.95))';
  }
  if (capacityLevel >= 30) {
    return 'drop-shadow(0 0 2px #fff) drop-shadow(0 0 5px rgba(249,115,22,0.85))';
  }
  return 'drop-shadow(0 0 2px #fff) drop-shadow(0 0 4px rgba(59,130,246,0.75))';
}

// ────────────────────────────────────────────────────────────────────────────

interface MapMarkerProps {
  venue: Venue;
  onClick: (venue: Venue) => void;
  onOpenDrawer: (venue: Venue) => void;
  showHeatZone?: boolean;
  forceHide?: boolean;
  zoom?: number; // fractional zoom from parent — used for smooth icon fade-in
}

export function MapMarker({ venue, onClick, onOpenDrawer, showHeatZone = true, forceHide = false, zoom: zoomProp }: MapMarkerProps) {
  const map = useMap();
  // Internal zoom tracks zoomend only — used for radius calculations (avoids SVG redraws every frame)
  const [zoomInternal, setZoomInternal] = useState(map.getZoom());

  useEffect(() => {
    const handleZoom = () => setZoomInternal(map.getZoom());
    map.on("zoomend", handleZoom);
    return () => {
      map.off("zoomend", handleZoom);
    };
  }, [map]);

  // zoomProp: fractional, updates every animation frame (passed from parent via zoom event)
  // zoomInternal: integer/discrete, updates after gesture ends (zoomend)
  const iconZoom = zoomProp ?? zoomInternal;

  const isUser = venue.id === -1;
  const isSponsored = venue.isSponsored;

  const venueClosed = useMemo(() => {
    if (isUser) return false;
    return (venue as any).isCurrentlyOpen === false;
  }, [(venue as any).isCurrentlyOpen, isUser]);

  const noData = useMemo(() => {
    if (isUser) return false;
    return venue.capacityLevel === 0 && (venue as any).isCurrentlyOpen !== false;
  }, [venue, isUser]);

  let fillColor = venueClosed ? "#06b6d4" : "#ef4444";

  // Radius uses discrete internal zoom to avoid recomputing SVG paths every animation frame
  const radiusInMeters = zoomInternal > 15 ? 100 : 500;
  const latRad = venue.latitude * (Math.PI / 180);
  const zoomLevelPixels = radiusInMeters / (156543.03392 * Math.cos(latRad) / Math.pow(2, zoomInternal));
  const finalRadius = isUser ? 8 : Math.max(15, zoomLevelPixels);

  if (forceHide && !isUser && !isSponsored) return null;

  const statusText = venueClosed ? 'Closed' : noData ? 'No data' : `${venue.capacityLevel}%`;
  const statusColor = venueClosed ? 'text-cyan-500' : noData ? 'text-zinc-400' : venue.capacityLevel >= 70 ? 'text-red-500' : venue.capacityLevel >= 30 ? 'text-orange-500' : 'text-blue-500';

  const venuePopup = !isUser ? (
    <Popup
      className="venue-popup"
      closeButton={false}
      autoPan={false}
    >
      <button
        data-testid={`popup-venue-${venue.id}`}
        onClick={(e) => {
          e.stopPropagation();
          map.closePopup();
          onOpenDrawer(venue);
        }}
        className="flex flex-col items-center gap-0.5 cursor-pointer min-w-[100px] text-center"
      >
        <span className="font-display font-bold text-sm text-white leading-tight">{venue.name}</span>
        <span className={`text-xs font-semibold ${statusColor}`}>{statusText}</span>
      </button>
    </Popup>
  ) : null;

  if (!isUser && !showHeatZone && !isSponsored) {
    // Smooth fade-in driven by fractional zoom from parent:
    //   iconZoom < 13  → 0   (invisible — no DOM below this threshold)
    //   iconZoom = 14  → 0.5 (ghost)
    //   iconZoom ≥ 15  → 1.0 (fully visible)
    const iconOpacity = Math.min(1, Math.max(0, (iconZoom - 13) / 2));

    // Don't mount DOM nodes at very wide zoom — avoids thousands of SVG elements at city-level view.
    // Mounting at Z13 (iconOpacity=0) means the CSS transition fires smoothly when Z14 is reached.
    if (iconZoom < 13) return null;

    // Solid-colour SVG icon: fill and glow driven by capacity tier
    const iconFill = getIconFill(venueClosed, noData, venue.capacityLevel);
    const iconFilter = getIconFilter(venueClosed, noData, venue.capacityLevel);

    // Blend status-driven base opacity with the continuous zoom-driven iconOpacity
    const baseOpacity = venueClosed ? 0.45 : noData ? 0.35 : 1;
    const effectiveOpacity = baseOpacity * iconOpacity;

    const iconStyle: CSSProperties = {
      opacity: effectiveOpacity,
      transition: 'opacity 0.3s ease',
      filter: iconFilter,
      display: 'block',
    };

    const VenueIcon = venue.type === 'bar'
      ? MartiniGlassIcon
      : venue.type === 'pub'
      ? PintGlassIcon
      : PartyPopperIcon;

    const isInteractive = iconOpacity >= 0.1;
    return (
      <CircleMarker
        center={[venue.latitude, venue.longitude]}
        radius={15}
        pathOptions={{
          color: 'transparent',
          fillColor: 'transparent',
          fillOpacity: 0,
          // SVG pointer-events:none when not interactive so the invisible circle
          // doesn't block clicks on map elements below it
          className: isInteractive ? '' : 'pointer-events-none',
        }}
        eventHandlers={isInteractive ? { click: () => onClick(venue) } : {}}
      >
        <Tooltip
          permanent
          direction="center"
          className="bg-transparent border-0 shadow-none !p-0 !bg-none pointer-events-none"
        >
          <span style={iconStyle}>
            <VenueIcon fill={iconFill} size={24} />
          </span>
        </Tooltip>
        {isInteractive ? venuePopup : null}
      </CircleMarker>
    );
  }

  return (
    <>
      {(!forceHide || isUser) && showHeatZone && (
        <CircleMarker
          center={[venue.latitude, venue.longitude]}
          pathOptions={{
            color: isUser ? '#ffffff' : 'transparent',
            fillColor: isUser ? '#3b82f6' : fillColor,
            fillOpacity: isUser ? 1 : venueClosed ? 0.15 : noData ? 0.25 : 0.5,
            weight: isUser ? 2 : 0,
            className: isUser ? "" : "heatmap-blob heatmap-pulse"
          }}
          radius={finalRadius}
          eventHandlers={{
            click: () => !isUser && onClick(venue),
          }}
        >
          {venuePopup}
        </CircleMarker>
      )}
      {isSponsored && (
        <CircleMarker
          center={[venue.latitude, venue.longitude]}
          radius={15}
          pathOptions={{
            color: 'transparent',
            fillColor: 'transparent',
            fillOpacity: 0,
          }}
          eventHandlers={{
            click: () => onOpenDrawer(venue),
          }}
        >
          <Tooltip
            permanent
            direction="center"
            className="bg-transparent border-0 shadow-none !p-0 !bg-none pointer-events-none sponsored-marker-tooltip"
          >
            {(() => {
              const SponsoredIcon =
                venue.type === 'bar' ? MartiniGlassIcon :
                venue.type === 'pub' ? PintGlassIcon :
                PartyPopperIcon;
              return (
                <span className="sponsored-vibrate relative z-[2000]" style={{ filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.9))' }}>
                  <SponsoredIcon fill="#ffffff" size={24} />
                </span>
              );
            })()}
          </Tooltip>
        </CircleMarker>
      )}
    </>
  );
}
